import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import moment from "moment";
import { OwAccordion } from "@components/shared/OwAccordion/OwAccordion";
import { ReactHookInput } from "@components/shared/ReactHookInput/ReactHookInput";
import { TypeaheadFn } from "@components/shared/TypeaheadFn/TypeaheadFn";
import EndpointsEnum from "@models/EndpointsEnum";
import IUpdateSupplierIntegrationsDTO from "@models/DTO/IUpdateSupplierIntegrationsDTO";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwButton from "@components/shared/OwButton/OwButton";
import OwBadge from "@components/shared/OwBadge/OwBadge";
import UnanetService from "@services/UnanetService";
import IUnanetLegalEntity from "@models/Unanet/IUnanetLegalEntity";
import IUnanetOrganization from "@models/Unanet/IUnanetOrganization";
import IUnanetAccount from "@models/Unanet/IUnanetAccount";
import IUnanetExpenseType from "@models/Unanet/IUnanetExpenseType";
import unanetLogo from "@assets/images/unanet/unanet-logo.png";
import { Link } from "react-router";
import "./UnanetConnect.scss";
import { useAppContextNonNull } from "@contexts/AppContext";
import { OwIconTooltip } from "@components/shared/OwIconTooltip/OwIconTooltip";

interface UnanetFormData {
    unanet_base_url: string;
    unanet_customer_id: string;
    unanet_username: string;
    unanet_password: string;
}

const validationSchema = yup.object().shape({
    unanet_base_url: yup.string().required("Base URL is required").url("Must be a valid URL"),
    unanet_customer_id: yup.string().default(""),
    unanet_username: yup.string().default(""),
    unanet_password: yup.string().default(""),
});

type Props = {};

export const UnanetConnect = (props: Props) => {
    const { supplierModel, userModel, socket } = useAppContextNonNull();

    const si = supplierModel.Supplier_Integration;

    const {
        register,
        handleSubmit,
        getValues,
        formState: { errors, dirtyFields },
    } = useForm<UnanetFormData>({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        resolver: yupResolver(validationSchema) as any,
        defaultValues: {
            unanet_base_url: si.unanet_base_url || "",
            unanet_customer_id: "",
            unanet_username: "",
            unanet_password: "",
        },
    });

    const [isSaving, setIsSaving] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
    const [testConnectionSucceeded, setTestConnectionSucceeded] = useState(false);

    // Dropdown state — managed outside react-hook-form (TypeaheadFn is callback-based)
    const [legalEntities, setLegalEntities] = useState<IUnanetLegalEntity[]>([]);
    const [selectedLegalEntity, setSelectedLegalEntity] = useState<IUnanetLegalEntity | undefined>(
        si.unanet_legal_entity_key != null
            ? ({
                  key: si.unanet_legal_entity_key,
                  name: `${si.unanet_legal_entity_name ?? ""} (${si.unanet_legal_entity_key})`,
              } as IUnanetLegalEntity)
            : undefined
    );
    const [organizations, setOrganizations] = useState<IUnanetOrganization[]>([]);
    const [selectedOrganization, setSelectedOrganization] = useState<IUnanetOrganization | undefined>(
        si.unanet_default_org_key != null
            ? ({
                  key: si.unanet_default_org_key,
                  name: `${si.unanet_default_org_name ?? ""} (${si.unanet_default_org_key})`,
              } as IUnanetOrganization)
            : undefined
    );
    const [accounts, setAccounts] = useState<IUnanetAccount[]>([]);
    const [selectedAccount, setSelectedAccount] = useState<IUnanetAccount | undefined>(
        si.unanet_ar_account_key != null
            ? ({
                  key: si.unanet_ar_account_key,
                  description: `${si.unanet_ar_account_name ?? ""} (${si.unanet_ar_account_key})`,
              } as IUnanetAccount)
            : undefined
    );
    const [expenseTypes, setExpenseTypes] = useState<IUnanetExpenseType[]>([]);
    const [selectedExpenseType, setSelectedExpenseType] = useState<IUnanetExpenseType | undefined>(
        si.unanet_default_expense_type_key != null
            ? ({
                  key: si.unanet_default_expense_type_key,
                  name: si.unanet_default_expense_type_name ?? "",
                  code: "",
              } as IUnanetExpenseType)
            : undefined
    );

    const dropdownsEnabled = !!si.unanet_is_connected || testConnectionSucceeded;

    // Lookup functions for TypeaheadFn — call API on each keystroke (700ms debounce in OwFnInput)
    const lookupLegalEntities = (value: string) => {
        if (!value || value.length < 1) {
            setLegalEntities([]);
            return;
        }
        UnanetService.searchLegalEntities({ name: value }, userModel, supplierModel)
            .then(setLegalEntities)
            .catch(() => setLegalEntities([]));
    };

    const lookupOrganizations = (value: string) => {
        if (!value || value.length < 1) {
            setOrganizations([]);
            return;
        }
        const lower = value.toLowerCase();
        UnanetService.getOrganizations({}, userModel, supplierModel)
            .then((orgs) =>
                setOrganizations(orgs.filter((o) => o.code?.toLowerCase().includes(lower) || o.name.toLowerCase().includes(lower)))
            )
            .catch(() => setOrganizations([]));
    };

    const lookupAccounts = (value: string) => {
        if (!value || value.length < 1) {
            setAccounts([]);
            return;
        }
        UnanetService.searchAccounts({ type: "ASSET", name: value }, userModel, supplierModel)
            .then(setAccounts)
            .catch(() => setAccounts([]));
    };

    const lookupExpenseTypes = (value: string) => {
        if (!value || value.length < 1) {
            setExpenseTypes([]);
            return;
        }
        const lower = value.toLowerCase();
        UnanetService.getExpenseTypes(userModel, supplierModel)
            .then((types) =>
                setExpenseTypes(types.filter((t) => t.name.toLowerCase().includes(lower) || t.code.toLowerCase().includes(lower)))
            )
            .catch(() => setExpenseTypes([]));
    };

    const toggleUnanet = () => {
        const payload: IUpdateSupplierIntegrationsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            // @ts-expect-error partial update
            Supplier_Integration: { has_unanet: !si.has_unanet },
        };
        socket.emit(EndpointsEnum.SupplierUpdateSupplierIntegrations, payload);
    };

    const onSubmit = (data: UnanetFormData) => {
        const payload: IUpdateSupplierIntegrationsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            // @ts-expect-error partial update
            Supplier_Integration: {
                has_unanet: true,
                unanet_base_url: data.unanet_base_url,
                unanet_legal_entity_key: selectedLegalEntity?.key ?? si.unanet_legal_entity_key ?? null,
                unanet_legal_entity_name:
                    selectedLegalEntity && selectedLegalEntity.key !== si.unanet_legal_entity_key
                        ? selectedLegalEntity.name
                        : (si.unanet_legal_entity_name ?? null),
                unanet_default_org_key: selectedOrganization?.key ?? si.unanet_default_org_key ?? null,
                unanet_default_org_name:
                    selectedOrganization && selectedOrganization.key !== si.unanet_default_org_key
                        ? selectedOrganization.name
                        : (si.unanet_default_org_name ?? null),
                unanet_ar_account_key: selectedAccount?.key ?? si.unanet_ar_account_key ?? null,
                unanet_ar_account_name:
                    selectedAccount && selectedAccount.key !== si.unanet_ar_account_key
                        ? selectedAccount.description
                        : (si.unanet_ar_account_name ?? null),
                unanet_default_expense_type_key: selectedExpenseType?.key ?? si.unanet_default_expense_type_key ?? null,
                unanet_default_expense_type_name:
                    selectedExpenseType && selectedExpenseType.key !== si.unanet_default_expense_type_key
                        ? selectedExpenseType.name
                        : (si.unanet_default_expense_type_name ?? null),
            },
        };

        if (dirtyFields.unanet_customer_id && data.unanet_customer_id) {
            payload.Supplier_Integration.unanet_customer_id = data.unanet_customer_id;
        }
        if (dirtyFields.unanet_username && data.unanet_username) {
            payload.Supplier_Integration.unanet_username = data.unanet_username;
        }
        if (dirtyFields.unanet_password && data.unanet_password) {
            payload.Supplier_Integration.unanet_password = data.unanet_password;
        }

        socket.emit(EndpointsEnum.SupplierUpdateSupplierIntegrations, payload);
        setIsSaving(true);
        setTimeout(() => setIsSaving(false), 2000);
    };

    const testConnection = async () => {
        setIsTesting(true);
        setTestResult(null);

        try {
            const values = getValues();
            const body: Record<string, string> = { unanet_base_url: values.unanet_base_url };

            if (dirtyFields.unanet_customer_id && values.unanet_customer_id) {
                body.unanet_customer_id = values.unanet_customer_id;
            }
            if (dirtyFields.unanet_username && values.unanet_username) {
                body.unanet_username = values.unanet_username;
            }
            if (dirtyFields.unanet_password && values.unanet_password) {
                body.unanet_password = values.unanet_password;
            }

            const data = await UnanetService.testConnection(body, userModel, supplierModel);

            if (data.success && data.user) {
                setTestResult({
                    success: true,
                    message: `${data.user.firstName} ${data.user.lastName} — ${data.user.organization?.name ?? ""}`,
                });
                setTestConnectionSucceeded(true);
            } else {
                setTestResult({ success: false, message: data.error || "Connection failed" });
            }
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : "Connection failed";
            setTestResult({ success: false, message });
        } finally {
            setIsTesting(false);
        }
    };

    const credentialIndicator = (storedValue: string | null | undefined) => {
        if (storedValue === "true") {
            return <OwLabel title={"set"} leftIconClass={"fas fa-check-circle text-success"} />;
        }
        return <OwLabel title={"not_set"} leftIconClass={"fas fa-times-circle text-danger"} />;
    };

    return (
        <div className={"UnanetConnect"}>
            <OwAccordion
                hideChevron={!si.has_unanet}
                leftImage={unanetLogo}
                leftImageHeight={60}
                leftImageAlt={"Unanet"}
                fullWidthClick={true}
                rightElem={
                    <div className={"ms-auto d-flex align-items-center gap-2"}>
                        {si.unanet_is_connected && (
                            <Link
                                className={"btn btn-sm btn-warning"}
                                to={"/business/integrations/unanet/sync-status"}
                                onClick={(e) => e.stopPropagation()}>
                                <i className={"fas fa-sync-alt me-1"} />
                                <OwLabel title={"unanet_sync_status"} noBottomMargin />
                            </Link>
                        )}
                        <OwToggle active={!!si.has_unanet} name={"toggleUnanet"} fn={toggleUnanet} label={""} />
                    </div>
                }>
                {/* Connection Status */}
                <div className={"UnanetConnect__status mb-3"}>
                    <OwBadge
                        title={si.unanet_is_connected ? "connected" : "disconnected"}
                        bgColor={si.unanet_is_connected ? "success" : "danger"}
                    />
                    {si.unanet_last_successful_auth_at && (
                        <span className={"text-muted small"}>
                            <OwLabel title={"last_connected"} /> {moment(si.unanet_last_successful_auth_at).fromNow()}
                        </span>
                    )}
                    {si.unanet_last_sync_at && (
                        <span className={"text-muted small"}>
                            <OwLabel title={"last_sync"} /> {moment(si.unanet_last_sync_at).fromNow()}
                        </span>
                    )}
                </div>

                {/* Error Alert */}
                {si.unanet_last_error && (
                    <div className={"alert alert-warning mb-3"}>
                        {si.unanet_last_error}
                        {si.unanet_last_error_at && <small className={"ms-2"}>({moment(si.unanet_last_error_at).fromNow()})</small>}
                    </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)}>
                    {/* Base URL */}
                    <div className={"mb-2"}>
                        <OwLabel title={"unanet_base_url"} showIsReqAsterik={true} />
                        <ReactHookInput
                            name={"unanet_base_url"}
                            register={register("unanet_base_url")}
                            error={errors.unanet_base_url}
                            placeholder={"https://your-instance.unanet.io"}
                            skipTranslate={true}
                        />
                    </div>

                    {/* Username */}
                    <div className={"mb-2"}>
                        <OwLabel title={"username"} showIsReqAsterik={true} />
                        <ReactHookInput
                            name={"unanet_username"}
                            register={register("unanet_username")}
                            placeholder={si.unanet_username === "true" ? "••••••••" : ""}
                            skipTranslate={true}
                            autoComplete={"off"}
                        />
                        {credentialIndicator(si.unanet_username)}
                    </div>

                    {/* Password */}
                    <div className={"mb-2"}>
                        <OwLabel title={"password"} showIsReqAsterik={true} />
                        <div className={"d-flex"}>
                            <ReactHookInput
                                name={"unanet_password"}
                                register={register("unanet_password")}
                                type={showPassword ? "text" : "password"}
                                placeholder={si.unanet_password === "true" ? "••••••••" : ""}
                                skipTranslate={true}
                                autoComplete={"new-password"}
                            />
                            <button
                                type={"button"}
                                className={"btn btn-outline-secondary btn-sm ms-2"}
                                onClick={() => setShowPassword(!showPassword)}>
                                <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"} />
                            </button>
                        </div>
                        {credentialIndicator(si.unanet_password)}
                    </div>

                    {/* Customer ID */}
                    <div className={"mb-2"}>
                        <OwLabel title={"unanet_customer_id"} />{" "}
                        <OwIconTooltip
                            tooltipContent={"unanet_customer_id_tooltip"}
                            translateContent={true}
                            iconClassName="fas fa-info-circle"
                        />
                        <ReactHookInput
                            name={"unanet_customer_id"}
                            register={register("unanet_customer_id")}
                            placeholder={si.unanet_customer_id === "true" ? "••••••••" : ""}
                            skipTranslate={true}
                            autoComplete={"off"}
                        />
                        {credentialIndicator(si.unanet_customer_id)}
                    </div>

                    {/* Legal Entity Key */}
                    <div className={"mb-2"}>
                        <OwLabel title={"unanet_legal_entity_key"} />
                        {dropdownsEnabled ? (
                            <TypeaheadFn
                                lookupFn={lookupLegalEntities}
                                availableOptions={legalEntities}
                                selectedOption={selectedLegalEntity}
                                selectedOptionFn={(item: IUnanetLegalEntity) => setSelectedLegalEntity(item)}
                                renderFn={(item: IUnanetLegalEntity) => (
                                    <span>
                                        {item.code} - {item.name}
                                    </span>
                                )}
                                titleProp={"name"}
                                valueProp={"key"}
                                placeholder={"search_legal_entities"}
                                isScrollable={true}
                                maxHeight={300}
                            />
                        ) : (
                            <input
                                className={"form-control form-control-sm"}
                                value={
                                    si.unanet_legal_entity_key != null
                                        ? `${si.unanet_legal_entity_name ?? ""} (${si.unanet_legal_entity_key})`
                                        : ""
                                }
                                disabled={true}
                                readOnly={true}
                            />
                        )}
                    </div>

                    {/* Default Org Key */}
                    <div className={"mb-2"}>
                        <OwLabel title={"unanet_default_org_key"} />
                        {dropdownsEnabled ? (
                            <TypeaheadFn
                                lookupFn={lookupOrganizations}
                                availableOptions={organizations}
                                selectedOption={selectedOrganization}
                                selectedOptionFn={(item: IUnanetOrganization) => setSelectedOrganization(item)}
                                renderFn={(item: IUnanetOrganization) => (
                                    <span>
                                        {item.code} - {item.name}
                                    </span>
                                )}
                                titleProp={"name"}
                                valueProp={"key"}
                                placeholder={"search_organizations"}
                                isScrollable={true}
                                maxHeight={300}
                            />
                        ) : (
                            <input
                                className={"form-control form-control-sm"}
                                value={
                                    si.unanet_default_org_key != null
                                        ? `${si.unanet_default_org_name ?? ""} (${si.unanet_default_org_key})`
                                        : ""
                                }
                                disabled={true}
                                readOnly={true}
                            />
                        )}
                    </div>

                    {/* AR Account Key */}
                    <div className={"mb-2"}>
                        <OwLabel title={"unanet_ar_account_key"} />
                        {dropdownsEnabled ? (
                            <TypeaheadFn
                                lookupFn={lookupAccounts}
                                availableOptions={accounts}
                                selectedOption={selectedAccount}
                                selectedOptionFn={(item: IUnanetAccount) => setSelectedAccount(item)}
                                renderFn={(item: IUnanetAccount) => (
                                    <span>
                                        {item.code} - {item.description}
                                    </span>
                                )}
                                titleProp={"description"}
                                valueProp={"key"}
                                placeholder={"search_accounts"}
                                isScrollable={true}
                                maxHeight={300}
                            />
                        ) : (
                            <input
                                className={"form-control form-control-sm"}
                                value={
                                    si.unanet_ar_account_key != null
                                        ? `${si.unanet_ar_account_name ?? ""} (${si.unanet_ar_account_key})`
                                        : ""
                                }
                                disabled={true}
                                readOnly={true}
                            />
                        )}
                    </div>

                    {/* Default Expense Type */}
                    <div className={"mb-2"}>
                        <OwLabel title={"unanet_default_expense_type"} />
                        {dropdownsEnabled ? (
                            <TypeaheadFn
                                lookupFn={lookupExpenseTypes}
                                availableOptions={expenseTypes}
                                selectedOption={selectedExpenseType}
                                selectedOptionFn={(item: IUnanetExpenseType) => setSelectedExpenseType(item)}
                                renderFn={(item: IUnanetExpenseType) => (
                                    <span>
                                        {item.code} - {item.name}
                                    </span>
                                )}
                                titleProp={"name"}
                                valueProp={"key"}
                                placeholder={"search_expense_types"}
                                isScrollable={true}
                                maxHeight={300}
                            />
                        ) : (
                            <input
                                className={"form-control form-control-sm"}
                                value={
                                    si.unanet_default_expense_type_key != null
                                        ? `${si.unanet_default_expense_type_name ?? ""} (${si.unanet_default_expense_type_key})`
                                        : ""
                                }
                                disabled={true}
                                readOnly={true}
                            />
                        )}
                    </div>

                    {/* Test Connection Result */}
                    {testResult && (
                        <div className={`alert alert-${testResult.success ? "success" : "danger"} mt-2`}>{testResult.message}</div>
                    )}

                    {/* Buttons */}
                    <div className={"mt-3"}>
                        <OwButton title={"save_settings"} fn={handleSubmit(onSubmit)} active={isSaving} disableOnActive={true} />
                        <OwButton
                            title={"test_connection"}
                            fn={testConnection}
                            active={isTesting}
                            disableOnActive={true}
                            className={"btn-secondary ms-3"}
                        />
                    </div>
                </form>

                {dropdownsEnabled && (
                    <div className="mt-3">
                        <hr className={"w-100"} />
                    </div>
                )}

                {/* {dropdownsEnabled && (
                    <div className={"d-flex gap-2"}>
                        <div className={""}>
                            <Link className={"btn btn-sm btn-warning"} to={"/business/integrations/unanet/sync-status"}>
                                <i className={"fas fa-sync-alt me-1"} />
                                <OwLabel title={"unanet_sync_status"} noBottomMargin />
                            </Link>
                        </div>
                    </div>
                )} */}
            </OwAccordion>
        </div>
    );
};
